import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Flower2, Menu, X, LogOut, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { NoteComposer } from "@/components/NoteComposer";

const links = [
  { name: "Garden", to: "/", testid: "nav-link-garden" },
  { name: "Roses", to: "/roses", testid: "nav-link-roses" },
  { name: "How It Works", to: "/how-it-works", testid: "nav-link-how-it-works" },
];

export function Nav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [pendingCompose, setPendingCompose] = useState(false);

  const active = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  const handleLeaveNote = () => {
    if (user) setComposeOpen(true);
    else { setPendingCompose(true); setAuthOpen(true); }
  };

  const handleSignIn = () => { setPendingCompose(false); setAuthOpen(true); };

  const onAuthSuccess = () => {
    if (pendingCompose) setComposeOpen(true);
    setPendingCompose(false);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-pink-500/20">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <Flower2 className="w-7 h-7 text-pink-400 group-hover:rotate-12 transition-transform" />
          <span className="font-serif text-2xl font-bold text-pink-50 tracking-tight text-glow">Garden</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                active(l.to) ? "text-pink-300 bg-pink-500/10" : "text-slate-300 hover:text-pink-200 hover:bg-white/5"
              }`}
            >
              {l.name}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Link
                to="/profile"
                data-testid="nav-link-profile"
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm text-slate-200 hover:bg-white/5 transition-all"
              >
                <UserIcon className="w-4 h-4 text-pink-400" />
                {user.name?.split(" ")[0] || "Profile"}
              </Link>
              <button
                data-testid="nav-logout-btn"
                onClick={logout}
                className="p-2 rounded-full text-slate-400 hover:text-pink-300 hover:bg-white/5 transition-all"
                title="Log out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              data-testid="nav-login-btn"
              onClick={handleSignIn}
              className="px-4 py-2 rounded-full text-sm text-slate-200 hover:text-pink-200 hover:bg-white/5 transition-all"
            >
              Sign in
            </button>
          )}
          <button
            data-testid="btn-leave-note-nav"
            onClick={handleLeaveNote}
            className="bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-500 hover:to-rose-400 text-white font-medium px-5 py-2.5 rounded-full shadow-lg shadow-pink-500/25 transition-all hover:scale-105 active:scale-95 text-sm"
          >
            Leave a Note
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 text-pink-200"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden border-t border-pink-500/20 bg-black/90 px-4 py-4 space-y-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`${l.testid}-mobile`}
              onClick={() => setOpen(false)}
              className="block px-4 py-3 rounded-xl text-slate-200 hover:bg-white/5"
            >
              {l.name}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/profile" onClick={() => setOpen(false)} className="block px-4 py-3 rounded-xl text-slate-200 hover:bg-white/5">
                My Profile
              </Link>
              <button onClick={() => { logout(); setOpen(false); }} className="w-full text-left px-4 py-3 rounded-xl text-slate-200 hover:bg-white/5">
                Log out
              </button>
            </>
          ) : (
            <button onClick={() => { handleSignIn(); setOpen(false); }} className="w-full text-left px-4 py-3 rounded-xl text-slate-200 hover:bg-white/5">
              Sign in
            </button>
          )}
          <button
            onClick={() => { handleLeaveNote(); setOpen(false); }}
            className="w-full bg-gradient-to-r from-pink-600 to-rose-500 text-white font-medium px-5 py-3 rounded-full mt-2"
          >
            Leave a Note
          </button>
        </div>
      )}

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={onAuthSuccess} />
      <NoteComposer open={composeOpen} onOpenChange={setComposeOpen} onCreated={() => navigate("/")} />
    </header>
  );
}
